import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ApiClient, type AuthMe } from './apiClient.tsx';
import { strings } from './strings.tsx';

/**
 * 사람은 세션(httpOnly 쿠키), 기계는 Bearer 키(결정46). 콘솔은 API 키를 더 이상
 * 다루지 않으므로 이 스토어는 "키를 들고 있는 것"에서 "로그인 여부를 아는 것"으로
 * 바뀐다. 쿠키는 httpOnly 라 JS 가 값을 읽을 수 없으므로, 로그인 여부는
 * GET /v1/auth/me 호출의 성패로만 판정한다.
 */
export type AuthStatus = 'checking' | 'authenticated' | 'anonymous';

interface AuthState {
  status: AuthStatus;
  me: AuthMe | null;
  /**
   * 로그인·로그아웃마다 증가하는 카운터(구 keyVersion, 결정42 → 결정46).
   *
   * useApiQuery 가 쿼리 키 뒤에 이 값을 섞는다. 세션이 바뀌었는데 쿼리 키가
   * 그대로면 React Query 에겐 같은 쿼리라 재조회가 나가지 않는다 — 이 값을
   * 바꿔야 "다른 쿼리"가 되어 로그인/로그아웃 직후 자동으로 다시 조회된다.
   */
  sessionVersion: number;
  /**
   * **쫓겨난 것인지, 원래 로그인 안 한 것인지** (결정2406).
   *
   * 상태만으로는 둘이 같은 `anonymous` 다. 그런데 사람에게는 전혀 다른 일이다 —
   * 앞의 것은 *"하던 일이 끊겼다"* 이고 뒤의 것은 그냥 방문이다. 이유를 안 적으면
   * 사용자는 **까닭 없이 튕겼다**고 읽는다.
   */
  sessionExpired: boolean;
  /**
   * 앱 전체가 공유하는 단일 ApiClient. useApiClient가 이 인스턴스를 그대로
   * 돌려준다 — testBearerToken을 준 테스트가 이 하나의 클라이언트를 통해
   * 로그인 화면 없이도 모든 화면의 데이터 조회에 Bearer를 실을 수 있게 한다.
   */
  client: ApiClient;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({
  children,
  initialStatus,
  testBearerToken,
}: {
  children: ReactNode;
  /**
   * 테스트가 부트스트랩(실제 /v1/auth/me 왕복) 없이 즉시 로그인/비로그인 상태를
   * 만들기 위한 것. 운영에서는 넘기지 않으므로 항상 실제 판정을 거친다.
   */
  initialStatus?: Exclude<AuthStatus, 'checking'>;
  /**
   * 테스트 전용 탈출구. 로그인 폼(또는 세션 쿠키) 없이 Bearer 키로 곧장 인증한다 —
   * 계약상 GET /v1/auth/me 는 세션·Bearer 둘 다 허용하므로(03 §11.2) 이 값을
   * 주면 부트스트랩이 그대로 성공한다. 화면 단위 테스트(testUtils)의 기본값과
   * 라이브 e2e(authOverride)가 함께 쓴다 — 로그인 자체를 검증하는 테스트만
   * 이 값을 비우고 실제 로그인 흐름을 탄다.
   */
  testBearerToken?: string;
}) {
  const [status, setStatus] = useState<AuthStatus>(initialStatus ?? 'checking');
  /*
   * 콜백이 client 를 다시 만들게 하면 안 된다 — client 가 바뀌면 모든 쿼리가 다시 돈다.
   * ref 로 한 겹 두고 client 는 그대로 둔다.
   */
  const expiredRef = useRef<() => void>(() => {});
  const client = useMemo(
    () =>
      new ApiClient(
        undefined,
        testBearerToken !== undefined ? () => testBearerToken : undefined,
        undefined,
        () => expiredRef.current(),
      ),
    [testBearerToken],
  );
  const [me, setMe] = useState<AuthMe | null>(null);
  const [sessionVersion, setSessionVersion] = useState(0);
  const [sessionExpired, setSessionExpired] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const result = await client.me();
      setMe(result);
      setStatus('authenticated');
    } catch {
      setMe(null);
      setStatus('anonymous');
    }
  }, [client]);

  useEffect(() => {
    // initialStatus를 받은 테스트는 부트스트랩을 건너뛴다 — 이미 상태가 확정돼 있다.
    if (initialStatus !== undefined) return;
    void refresh();
    // 마운트 시 1회만. refresh는 client에만 의존하고 client는 testBearerToken가
    // 바뀔 때만 새로 만들어지므로 여기서 다시 실행할 이유가 없다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * **세션이 도중에 끊긴 것을 화면에 반영한다** (결정2406).
   *
   * ⚠ 처리기를 `useEffect` 에서 심는다. 렌더 중에 ref 를 쓰면 동시 렌더에서 **버려질
   * 렌더의 값**이 남을 수 있다. 요청은 마운트 뒤에만 나가므로 효과가 먼저 돈다.
   *
   * ⚠ 이미 anonymous 면 아무 일도 안 한다 — 로그인 화면의 401 로 흔들리면 안 된다.
   * `sessionVersion` 을 올려 캐시를 갈아 준다: 다시 로그인한 뒤 **앞 사람의 값**이
   * 남아 보이면 안 된다.
   */
  const statusRef = useRef(status);
  useEffect(() => {
    statusRef.current = status;
    expiredRef.current = () => {
      if (statusRef.current !== 'authenticated') return;
      setMe(null);
      setStatus('anonymous');
      setSessionExpired(true);
      setSessionVersion((v) => v + 1);
    };
  }, [status]);

  const login = useCallback(
    async (username: string, password: string) => {
      // 실패하면 여기서 던져 LoginScreen이 사유를 그대로 보여줄 수 있게 한다.
      await client.login(username, password);
      setSessionExpired(false);
      await refresh();
      setSessionVersion((v) => v + 1);
    },
    [client, refresh],
  );

  const logout = useCallback(async () => {
    // 서버 무효화가 실패해도(네트워크 등) 화면은 로그아웃된 것으로 취급한다 —
    // 쿠키가 남아 있어도 다음 조회가 401이면 어차피 anonymous로 되돌아간다.
    await client.logout().catch(() => {});
    setMe(null);
    setStatus('anonymous');
    /*
     * 여기서 `setSessionExpired(false)` 를 부르지 **않는다.** 처음엔 넣었는데
     * 변이로 재 보니 **아무 검사도 안 죽었다** — 닿을 수 없는 줄이었다.
     * 만료는 곧바로 anonymous 로 만들고, anonymous 에는 로그아웃 단추가 없다.
     * 그러니 로그아웃이 불릴 수 있는 순간에 이 깃발은 **이미 거짓**이다.
     * ⚠ 만료를 anonymous 로 안 만들게 바꾸면 이 불변식이 깨진다 — 그때 되살려라.
     */
    setSessionVersion((v) => v + 1);
  }, [client]);

  const value = useMemo(
    () => ({ status, me, sessionVersion, sessionExpired, client, login, logout }),
    [status, me, sessionVersion, sessionExpired, client, login, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error(strings.authStore.useAuthOutsideProvider);
  return ctx;
}
